import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { device } from '@/constants/breakpoints';
import { ReactComponent as CloseIcon } from '@/assets/icons/close.icon.svg';
import OtpInput from 'react-otp-input';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { initiateTransfer } from '@/services/bankingApi';
import toast from 'react-hot-toast';
import SuccessPopup from '@/components/SuccessPopup';
import { Oval } from 'react-loader-spinner';
import { numberWithCommas } from '@/utils/helpers';

function BeneficiaryOtpModal({ showModal, setShowModal, selectedAccount, amount, remark, reset }) {
  const [otp, setOtp] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const queryClient = useQueryClient();

  const handleMakeTransfer = useMutation(initiateTransfer, {
    onSuccess: (data) => {
      setShowModal(false);
      setOtp('');
      setShowSuccessModal(true);
      queryClient.invalidateQueries(['userWallet']);
      queryClient.invalidateQueries(['bankingAccount']);
      queryClient.invalidateQueries(['walletTransactions']);
      queryClient.invalidateQueries(['transferHistory']);
      reset({
        amount: '',
        remark: ''
      });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
      setOtp('');
      // reset({
      //   amount: '',
      //   remark: ''
      // });
    }
  });

  const onSubmit = () => {
    const data = {
      amount: parseFloat(amount || '00.00'),
      provider: 'providus',
      accountNumber: selectedAccount?.accountNumber,
      accountName: selectedAccount?.accountName,
      bankName: selectedAccount?.bankName,
      bankCode: selectedAccount?.bankCode,
      pin: otp,
      remark
    };
    handleMakeTransfer.mutate(data);
  };

  return (
    <>
      <AnimatePresence>
        {showModal && (
          <ModalView
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="modal-overlay">
            <motion.div
              initial={{ y: 1000 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              transition={{ duration: 0.3 }}
              className="modal"
              onClick={(e) => e.stopPropagation()}>
              {/* ======== Content ======== */}
              <ModalContent>
                <ContentView>
                  <div className="header">
                    <h3>Confirm transfer</h3>
                    <button
                      type="button"
                      onClick={() => {
                        reset({
                          amount: '',
                          remark: ''
                        });
                        setShowModal(false);
                      }}>
                      <CloseIcon />
                    </button>
                  </div>

                  <div className="content">
                    <div className="warn-view">
                      <p>
                        You are about to transfer{' '}
                        <span>₦{numberWithCommas(amount || '00.00')}</span> to{' '}
                        <span>{selectedAccount?.accountName}</span> with account number{' '}
                        <span>{selectedAccount?.accountNumber}</span> from your Providus bank
                        account. Please confirm the details before entering your transaction PIN.
                      </p>
                    </div>

                    <TitleView>
                      <p>Enter your transaction PIN below</p>
                    </TitleView>

                    <FormView>
                      <div className="input-view">
                        <OtpInput
                          value={otp}
                          onChange={setOtp}
                          numInputs={6}
                          renderSeparator={<span></span>}
                          renderInput={(props) => <input {...props} />}
                          containerStyle="otp-input"
                          inputType="number"
                          skipDefaultStyles
                          shouldAutoFocus
                        />
                      </div>
                      <div className="action-btn">
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => {
                            reset({
                              amount: '',
                              remark: ''
                            });
                            setShowModal(false);
                          }}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={otp.length < 6}
                          onClick={onSubmit}>
                          {handleMakeTransfer.isLoading ? (
                            <Oval color="#fff" height={20} width={20} />
                          ) : (
                            'Confirm transfer'
                          )}
                        </button>
                      </div>
                    </FormView>
                  </div>
                </ContentView>
              </ModalContent>
            </motion.div>
          </ModalView>
        )}
      </AnimatePresence>

      <SuccessPopup
        showModal={showSuccessModal}
        setShowModal={setShowSuccessModal}
        mainText="Transfer successful"
        subText="
        Your transfer was successful. You can view your transaction history to see details of this transfer.
      "
      />
    </>
  );
}

export default BeneficiaryOtpModal;

BeneficiaryOtpModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired
};

const ModalView = styled(motion.div)`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9009;
  display: flex;
  align-items: center;
  justify-content: center;

  .modal {
    background-color: ${(props) => props.theme.colors?.white};
    z-index: 30000;
    position: fixed;
    border-radius: 16px;
    width: 550px;
    padding: 30px 0px;
    padding-top: 0;
    padding-bottom: 20px;
    border-radius: 10px;
    min-height: 300px;
    max-height: 90%;
    overflow-y: scroll;

    @media ${device.phone} {
      width: 96%;
    }
  }
`;

const ModalContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  margin: 0;
  justify-content: flex-start;
  flex-direction: column;
  padding: 20px 30px;

  @media ${device.phone} {
    padding: 20px 16px;
  }

  .header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 {
      font-size: 1.1rem;
      color: ${(props) => props.theme.colors?.secondary};
      font-weight: 600;
    }
  }
`;

const ContentView = styled.div`
  width: 100%;

  .content {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    margin-top: 30px;

    .warn-view {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      background-color: ${({ theme }) => theme.colors.primaryFocus};
      padding: 20px;
      border-radius: 10px;
      margin-bottom: 30px;

      p {
        font-size: 1rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors.secondary};
        line-height: 20px;
        max-width: 100%;

        span {
          font-weight: 600;
        }
      }
    }
  }
`;

const TitleView = styled.div`
  width: 100%;

  h3 {
    font-size: 1.1rem;
    color: ${(props) => props.theme.colors?.secondary};
    font-weight: 600;
    margin-bottom: 8px;
  }

  p {
    font-size: 1rem;
    color: ${(props) => props.theme.colors?.secondary};
    font-weight: 400;
    margin-bottom: 20px;
  }
`;

const FormView = styled.form`
  width: 100%;

  .input-view {
    width: 100%;
    margin-bottom: 30px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;

    input {
      width: 100%;
    }

    label {
      font-size: 0.875rem;
      font-weight: 400;

      span {
        font-size: 0.75rem;
        color: ${({ theme }) => theme.colors?.info};
      }
    }

    .error-label {
      font-size: 0.875rem;
      color: ${({ theme }) => theme.colors?.error};
      margin-top: 5px;
    }

    .input-info-view {
      width: 100%;
      display: flex;
      margin-top: 5px;

      span {
        font-size: 0.875rem;
        color: ${({ theme }) => theme.colors?.info};
      }
    }

    .otp-input {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 0px;

      input {
        width: calc(100% / 6 - 10px);
        height: 56px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        border: none;
        font-size: 1rem;
        font-weight: 400;
        color: ${({ theme }) => theme.colors?.secondary};
        text-align: center;
        background-color: ${({ theme }) => theme.colors?.inputBackground};
        -webkit-text-security: disc;

        &:focus {
          outline: 1px solid ${({ theme }) => theme.colors?.primary};
          border: none;
        }
      }
    }
  }

  .resend-view {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 30px;

    span {
      font-size: 0.875rem;
      color: ${({ theme }) => theme.colors?.secondary};
      font-weight: 400;
    }

    .resend {
      font-size: 0.875rem;
      color: ${({ theme }) => theme.colors?.primary};
      font-weight: 600;
      cursor: pointer;
      background-color: transparent;
    }
  }

  .action-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 100px;

    button {
      margin-left: 10px;
      height: 56px;
      border-radius: 10px;
      padding: 12px 32px;
      font-size: 1rem;
      font-weight: 600;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .btn-outline {
      background-color: transparent;
      border: 1px solid ${({ theme }) => theme.colors?.primary};
      color: ${({ theme }) => theme.colors?.primary};
    }

    .btn-primary {
      background-color: ${({ theme }) => theme.colors?.primary};
      border: 1px solid ${({ theme }) => theme.colors?.primary};
      color: ${({ theme }) => theme.colors?.white};
    }
  }
`;
